import { useEffect } from "react";

import { completeMission } from "../game";
import type { DemoDecision, DemoRunResult } from "../types";

/**
 * The decision receipt.
 *
 * The audit trail is this project's central claim - every decision can answer "why?"
 * later - so the panel stays, but reframed as something everyone already understands: a
 * receipt. Every field is renamed into plain language with the technical term kept small,
 * reason codes are translated, and the raw-JSON dump is gone (per feedback): the receipt
 * IS the projection of the response, there is nothing behind it the visitor needs.
 */

const REASON_LABELS: Record<string, string> = {
  RISK_SCORE_BELOW_REVIEW_THRESHOLD: "위험 점수가 기준선보다 낮음 → 자동 통과",
  RISK_SCORE_ABOVE_REVIEW_THRESHOLD: "위험 점수가 기준선을 넘음 → 사람에게",
  MODEL_UNAVAILABLE: "AI(점수 계산기)가 고장 → 안전하게 사람에게",
  FEATURE_UNAVAILABLE: "단서 수집 실패 → 안전하게 사람에게",
  POLICY_FALLBACK: "판정 규칙 오류 → 안전하게 사람에게",
  STEP_UP_PASSED: "휴대폰 본인확인 통과 → 자동 통과",
  STEP_UP_FAILED: "본인확인 실패 → 사람에게",
  STEP_UP_UNAVAILABLE: "본인확인을 완료할 수 없음 → 사람에게",
  STEP_UP_SUSPENDED: "자동화 권한이 회수된 상태 → 사람에게",
};

const CHANNEL_LABELS: Record<string, string> = {
  MOBILE: "모바일 앱",
  WEB: "인터넷뱅킹",
  ATM: "ATM",
  BRANCH: "은행 창구",
  API: "외부 연동",
};

interface ClueRow {
  label: string;
  value: string;
}

function clues(snapshot: Record<string, unknown>): ClueRow[] {
  const rows: ClueRow[] = [];
  if ("amount" in snapshot) {
    rows.push({
      label: "💰 금액",
      value: `${Number(snapshot.amount).toLocaleString()}원`,
    });
  }
  if ("hour_of_day" in snapshot) {
    const hour = Number(snapshot.hour_of_day);
    rows.push({
      label: "🕐 보낸 시각",
      value: `${hour}시${hour <= 5 ? " (새벽 — 위험 신호)" : ""}`,
    });
  }
  if ("is_new_payee" in snapshot) {
    rows.push({
      label: "👤 처음 보내는 상대?",
      value: snapshot.is_new_payee ? "예 (위험 신호)" : "아니오",
    });
  }
  if ("channel" in snapshot) {
    const raw = String(snapshot.channel);
    rows.push({ label: "📲 보낸 방법", value: CHANNEL_LABELS[raw] ?? raw });
  }
  if ("transaction_type" in snapshot) {
    rows.push({ label: "🏷️ 거래 종류", value: String(snapshot.transaction_type) });
  }
  return rows;
}

interface Props {
  result: DemoRunResult | null;
}

export function DecisionTrace({ result }: Props) {
  // A real receipt was issued (a stored decision came back): mission 6 verified.
  // Combo with mission 3 is intentional - breaking the system is what issues a receipt.
  const receiptIssued = Boolean(
    result && result.status !== "failed" && result.decisions.length > 0,
  );
  useEffect(() => {
    if (receiptIssued) completeMission("try-receipt");
  }, [receiptIssued]);

  if (!result) {
    return (
      <section className="card">
        <h3>🧾 판정 영수증</h3>
        <p className="muted">
          위에서 시나리오를 실행하면, 그 판정이 장부에 남긴 기록이 영수증처럼 표시됩니다.
        </p>
      </section>
    );
  }

  if (result.status === "failed") {
    return (
      <section className="card">
        <h3>🧾 판정 영수증</h3>
        <p>
          <strong>영수증이 없습니다 — 그리고 그게 정상입니다.</strong> 판정을 장부에
          저장하지 못했기 때문에, &quot;저장된 척&quot;하는 가짜 영수증을 만들지 않습니다.
          돈은 움직이지 않았고, 고객이 다시 시도해도 이중 처리되지 않아요.
        </p>
      </section>
    );
  }

  const decision: DemoDecision | undefined = result.decisions[result.decisions.length - 1];
  if (!decision) {
    return (
      <section className="card">
        <h3>🧾 판정 영수증</h3>
        <p className="muted">판정 결과가 없습니다.</p>
      </section>
    );
  }

  const clueRows = clues(decision.feature_snapshot ?? {});
  const approved = decision.outcome === "APPROVE";

  return (
    <section className="card">
      <h3>🧾 판정 영수증</h3>
      <p className="muted">
        방금 판정이 장부에 남긴 기록입니다. 이 영수증이 남는다는 것 자체가 이 시스템의
        핵심이에요 — 나중에 &quot;왜 그렇게 판정했어?&quot;라는 질문에 언제나 답할 수
        있습니다. (모든 거래는 서버가 만든 가상 값)
      </p>

      <dl className="trace-grid">
        <dt>최종 결과</dt>
        <dd>
          <span className={`badge ${approved ? "approve" : "review"}`}>
            {approved ? "✅ 통과" : "🧑‍💼 사람이 확인"}
          </span>{" "}
          <span className="muted">({decision.outcome})</span>
        </dd>

        <dt>위험 점수</dt>
        <dd>
          {decision.risk_score === null ? (
            "계산 못 함 (부품 고장 → 안전하게 사람에게)"
          ) : (
            <>
              <strong>{decision.risk_score.toFixed(2)}</strong> / 1.00 — 기준선{" "}
              {result.review_threshold.toFixed(2)}을{" "}
              {decision.risk_score >= result.review_threshold ? "넘어서" : "넘지 않아서"}{" "}
              위와 같이 판정
            </>
          )}
        </dd>

        <dt>판정 이유</dt>
        <dd>
          {decision.reason_codes.length === 0
            ? "—"
            : decision.reason_codes.map((code) => (
                <div key={code}>
                  {REASON_LABELS[code] ?? code}{" "}
                  <span className="flow-tech-inline">{code}</span>
                </div>
              ))}
        </dd>

        <dt>비상 모드였나?</dt>
        <dd>
          {decision.degraded
            ? "예 — 부품이 고장 나서, 자동 통과 대신 사람에게 보냈어요"
            : "아니오 — 모든 부품 정상"}
        </dd>

        <dt>재사용된 판정인가?</dt>
        <dd>
          {decision.replayed
            ? "예 — 같은 요청이 또 와서, 새로 판정하지 않고 처음 판정을 그대로 줬어요 (이중 처리 방지)"
            : "아니오 — 이번에 새로 내린 판정"}
        </dd>

        <dt>
          그때 쓰인 부품 버전
          <br />
          <span className="flow-tech-inline">model · feature · policy</span>
        </dt>
        <dd>
          <code>{decision.versions.model}</code> · <code>{decision.versions.feature}</code> ·{" "}
          <code>{decision.versions.policy}</code>
          <div className="muted">→ 이 버전 조합을 알기에, 몇 달 뒤에도 똑같이 재현 가능</div>
        </dd>
      </dl>

      <h4>🔎 AI가 본 단서</h4>
      {clueRows.length === 0 ? (
        <p className="muted">단서 수집 단계에서 실패해 저장된 단서가 없습니다.</p>
      ) : (
        <table className="evidence-table">
          <tbody>
            {clueRows.map((row) => (
              <tr key={row.label}>
                <td>{row.label}</td>
                <td>{row.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
